import { Button, Typography } from '@mui/material';
import { arrayMoveImmutable as arrayMove } from 'array-move';
import CoddingGroupListAddModal from 'components/private/codding-group/CoddingGroupListAddModal';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import axiosClient from 'lib/axiosClient';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import SortableComponent from '../../../components/shared/sortable/SortableComponent';

const CoddingGroupList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { guid } = useParams();
  const { can } = usePermissions();

  const profileGroupPermissions =
    permissions.general.categories.globalSettings.coddingSettings.profileGroup;

  const canEdit = can(profileGroupPermissions.edit);

  const [open, setOpen] = useState(false);
  const [groupDetails, setGroupDetails] = useState();
  const [allGroupsListData, setAllGroupsListData] = useState([]);

  const itemsRef = useRef([]);

  useEffect(() => {
    axiosClient
      .get(`/SettingProduct/CoddingProfile/CoddingSystemDetails?guid=${guid}`, {
        guid,
      })
      .then(response => {
        if (response) {
          const data = response.data?.value;

          setGroupDetails(data);
          itemsRef.current = data?.items ?? [];
        }
      })
      .catch(() => {
        enqueueSnackbar('خطا در دریافت لیست', {
          variant: 'error',
        });
      });
  }, [guid, enqueueSnackbar]);

  useEffect(() => {
    if (!canEdit) {
      return;
    }

    axiosClient
      .get('/SettingProduct/CoddingGroup/List', {
        page: '-1',
      })
      .then(response => {
        if (response) {
          setAllGroupsListData(response.data?.value?.list ?? []);
        }
      })
      .catch(() => {
        enqueueSnackbar('خطا در دریافت لیست', {
          variant: 'error',
        });
      });
  }, [canEdit, enqueueSnackbar]);

  const setItems = items => {
    itemsRef.current = items;

    setGroupDetails(oldData => ({
      ...oldData,
      items,
    }));
  };

  const update = async ({ items, rollbackItems }) => {
    try {
      await axiosClient.put(
        '/SettingProduct/CoddingProfile/CoddingSystemUpdate',
        {
          profileGuid: groupDetails?.guid,
          groupsGUID: items.map(item => item?.guid),
        },
      );

      setOpen(false);
      return true;
    } catch {
      enqueueSnackbar('عملیات به مشکل خورد', {
        variant: 'error',
      });

      if (rollbackItems) {
        setItems(rollbackItems);
      }

      return false;
    }
  };

  const onAdd = async item => {
    const currentItems = itemsRef.current;

    if (currentItems.some(i => i?.guid === item?.guid)) {
      enqueueSnackbar('این گروه قبلا اضافه شده', {
        variant: 'error',
      });

      return;
    }

    const newItem = {
      ...item,
      title: item.display,
    };

    const newItems = [...currentItems, newItem];

    setItems(newItems);

    await update({
      items: newItems,
      rollbackItems: currentItems,
    });
  };

  const onDragEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      return;
    }

    const currentItems = itemsRef.current;

    const newItems = arrayMove(currentItems, oldIndex, newIndex);

    setItems(newItems);

    await update({
      items: newItems,
      rollbackItems: currentItems,
    });
  };

  const onDelete = async item => {
    const currentItems = itemsRef.current;

    const newItems = currentItems.filter(
      currentItem => currentItem?.guid !== item?.guid,
    );

    setItems(newItems);

    await update({
      items: newItems,
      rollbackItems: currentItems,
    });
  };

  return (
    <>
      {canEdit ? (
        <Button
          onClick={() => setOpen(true)}
          variant="contained"
          color="primary"
        >
          افزودن
        </Button>
      ) : null}

      {groupDetails && groupDetails.items.length > 0 ? (
        <SortableComponent
          items={groupDetails.items ?? []}
          onDelete={canEdit ? onDelete : undefined}
          onDragEnd={canEdit ? onDragEnd : undefined}
          canDelete={canEdit}
          canReorder={canEdit}
        />
      ) : (
        <Typography
          sx={{ my: 4, mx: 'auto', width: '100%', textAlign: 'center' }}
          variant="body1"
        >
          آیتمی برای نمایش وجود ندارد
        </Typography>
      )}

      {canEdit ? (
        <CoddingGroupListAddModal
          onAdd={onAdd}
          codeList={allGroupsListData}
          open={open}
          handleClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
};

export default CoddingGroupList;
